import { ReparoApi } from '@api';
import PageHeader from '@components/common/PageHeader';
import AskAiButton from '@components/QuickHelpPanel/AskAiButton';
import { useWidgetContextCapture } from '@hooks/useWidgetContextCapture';
import { DatePopover } from '@components/ui/DateRange';
import FilterDropdown, { type DropdownOption } from '@components/ui/Dropdown';
import { DataSheet, DataSheetPagination, dataSheetTokens, type DataSheetColumn } from '@components/ui/DataSheet';
import ShareModal from '@components/ui/ShareModal';
import useAiAnalystContext from '@providers/AiAnalyst/useAiAnalystContext';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useSearchParams } from 'react-router';

import usePersistentState from '../../hooks/usePersistentState';
import { defaultLast4Weeks, formatDisplayRange } from './demand/dateUtils';
import { ConsultantCapability } from '../../constants/consultant-capability';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import useApp from '@providers/App/useApp';
import PurchaseOrderDetailDrawer, {
    StatusPill,
    formatDateDisplay,
    normalizeStatus,
    type PoType,
    type PurchaseOrderRow,
} from './PurchaseOrderDetailDrawer';
import { formatCount } from '@utils/currency';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

interface ChannelApiResponse {
    channelId?: string;
    abbreviationKey?: string;
    name?: string;
    type?: string;
}

interface VendorApiResponse {
    vendorId?: string;
    name?: string;
    type?: string;
}

interface PurchaseOrderListItemResponse {
    purchaseOrderId: string;
    channel?: string;
    vendor?: string;
    itemsCount: number;
    totalQuantity: number;
    actualQuantity?: number;
    excessQuantity?: number;
    orderDate: string;
    expiryDate?: string;
    actualDeliveryDate?: string;
    status: string;
    type: PoType;
    poRefNum?: string;
}

interface PurchaseOrderListResponse {
    data: PurchaseOrderListItemResponse[];
    total: number;
    page: number;
    pageSize: number;
    hasNext: boolean;
    wmsType?: string;
}

/* -------------------------------------------------------------------------- */
/*                              Helper utilities                              */
/* -------------------------------------------------------------------------- */

const tokens = dataSheetTokens;



/* -------------------------------------------------------------------------- */
/*                                 Page View                                  */
/* -------------------------------------------------------------------------- */

const PurchaseOrdersPage: React.FC = () => {
    const { currentUser } = useCurrentUser();
    const { consultantAccess } = useApp();
    const isConsultant = currentUser?.role === 'CONSULTANT';
    const [shareModal, setShareModal] = useState<{ resourceKey: string } | null>(null);

    // Determine which tabs consultant has access to
    const hasDemandAccess = !isConsultant || consultantAccess.includes(ConsultantCapability.TABLE_PO_DEMAND);
    const hasSupplyAccess = !isConsultant || consultantAccess.includes(ConsultantCapability.TABLE_PO_SUPPLY);

    const { captureContext: capturePO, addToChat: addPOToChat } = useWidgetContextCapture({
        componentName: 'PurchaseOrdersTable',
        label: 'Purchase Orders List',
    });

    // Build visible tabs based on access
    const visibleTabs = React.useMemo(() => {
        const tabs: Array<{ id: 'demand' | 'supply'; label: string }> = [];
        if (hasDemandAccess) tabs.push({ id: 'demand', label: 'Demand Side' });
        if (hasSupplyAccess) tabs.push({ id: 'supply', label: 'Supply Side' });
        return tabs;
    }, [hasDemandAccess, hasSupplyAccess]);

    // Default to first available tab
    const defaultSide = visibleTabs[0]?.id || 'demand';

    const {
        abortStream,
        setAiContext,
    } = useAiAnalystContext((context) => context.actions);

    const { showAISideBar } = useAiAnalystContext((context) => context.state);

    const [searchParams] = useSearchParams();

    const [side, setSide] = usePersistentState<'demand' | 'supply'>(
        'po_side_view',
        defaultSide,
    );

    const [demandFilters, setDemandFilters] = usePersistentState(
        'po_demand_filters',
        {
            range: defaultLast4Weeks(),
            search: '',
            channel: 'all',
            status: 'all',
            page: 1,
            pageSize: 20,
        },
    );

    const [showDemandCalendar, setShowDemandCalendar] = useState(false);

    const [supplyFilters, setSupplyFilters] = usePersistentState(
        'po_supply_filters',
        {
            range: defaultLast4Weeks(),
            search: '',
            vendor: 'all',
            status: 'all',
            fulfilment: 'all',
            page: 1,
            pageSize: 20,
        },
    );

    const [showSupplyCalendar, setShowSupplyCalendar] = useState(false);

    const [channelOptions, setChannelOptions] = useState<DropdownOption[]>([
        { label: 'All Channels', value: 'all' },
    ]);
    const [vendorOptions, setVendorOptions] = useState<DropdownOption[]>([
        { label: 'All Vendors', value: 'all' },
    ]);
    const [demandStatusOptions, setDemandStatusOptions] = useState<
        DropdownOption[]
    >([{ label: 'All Status', value: 'all' }]);
    const [supplyStatusOptions, setSupplyStatusOptions] = useState<
        DropdownOption[]
    >([{ label: 'All Status', value: 'all' }]);

    const [demandPos, setDemandPos] = useState<PurchaseOrderRow[]>([]);
    const [supplyPos, setSupplyPos] = useState<PurchaseOrderRow[]>([]);
    const [loadingDemand, setLoadingDemand] = useState(false);
    const [loadingSupply, setLoadingSupply] = useState(false);

    const [_demandHasNext, setDemandHasNext] = useState(false);
    const [demandTotal, setDemandTotal] = useState(0);

    const [_supplyHasNext, setSupplyHasNext] = useState(false);
    const [supplyTotal, setSupplyTotal] = useState(0);
    const [wmsType, setWmsType] = useState<string | undefined>();

    const [selectedRow, setSelectedRow] = useState<PurchaseOrderRow | null>(
        null,
    );
    const [deepLinkPoId, setDeepLinkPoId] = useState<string | null>(() =>
        searchParams.get('poId'),
    );

    const channelLabelLookup = useMemo(() => {
        const entries: [string, string][] = channelOptions
            .filter((opt) => opt.value !== 'all')
            .map((opt) => [String(opt.value), opt.label] as [string, string]);
        return new Map<string, string>(entries);
    }, [channelOptions]);

    const filteredSupplyPos = useMemo(() => {
        if (supplyFilters.status === 'all') return supplyPos;
        const targetStatus = normalizeStatus(supplyFilters.status);
        return supplyPos.filter((po) => po.status === targetStatus);
    }, [supplyPos, supplyFilters.status]);

    const getSelectedChannelLabel = useCallback(() => {
        const found = channelOptions.find(
            (opt) => String(opt.value) === String(demandFilters.channel),
        );
        return found?.label || demandFilters.channel;
    }, [channelOptions, demandFilters.channel]);

    const formatStatusForApi = useCallback((statusValue: string) => {
        if (statusValue === 'all') return [];
        return [statusValue];
    }, []);

    /* -------------------------------- Fetchers ------------------------------- */

    useEffect(() => {
        const fetchChannels = async () => {
            try {
                const channels =
                    ((await ReparoApi({
                        method: 'GET',
                        url: '/channels?limit=200',
                        body: undefined,
                    })) as ChannelApiResponse[] | null) ?? [];

                const mapped = channels
                    .filter((channel) => channel.type === 'B2B')
                    .map((channel) => {
                        const key =
                            channel.abbreviationKey ||
                            channel.channelId ||
                            channel.name;
                        const label =
                            channel.abbreviationKey ||
                            channel.name ||
                            channel.channelId;
                        if (!key || !label) return null;
                        return { label, value: String(key) } as DropdownOption;
                    })
                    .filter((opt): opt is DropdownOption =>
                        Boolean(opt && opt.value),
                    );

                const deduped = Array.from(
                    new Map(mapped.map((opt) => [opt.value, opt])).values(),
                );

                setChannelOptions([
                    { label: 'All Channels', value: 'all' },
                    ...deduped,
                ]);
            } catch {
                setChannelOptions([{ label: 'All Channels', value: 'all' }]);
            }
        };

        const fetchVendors = async () => {
            try {
                const vendors =
                    ((await ReparoApi({
                        method: 'GET',
                        url: '/vendors?limit=200',
                        body: undefined,
                    })) as VendorApiResponse[] | null) ?? [];

                const mapped = vendors
                    .map((vendor) => {
                        if (!vendor.vendorId && !vendor.name) return null;
                        return {
                            label: vendor.name || vendor.vendorId || '',
                            value: vendor.vendorId || vendor.name || '',
                        } as DropdownOption;
                    })
                    .filter((opt): opt is DropdownOption =>
                        Boolean(opt && opt.value),
                    );

                setVendorOptions([
                    { label: 'All Vendors', value: 'all' },
                    ...mapped,
                ]);
            } catch {
                setVendorOptions([{ label: 'All Vendors', value: 'all' }]);
            }
        };

        void fetchChannels();
        void fetchVendors();
    }, []);

    useEffect(() => {
        const fetchDemandStatuses = async () => {
            try {
                const statuses =
                    ((await ReparoApi({
                        method: 'POST',
                        url: '/purchase-orders/statuses/list',
                        body: JSON.stringify({ type: 'OUTGOING' as PoType }),
                    })) as unknown as string[] | null) ?? [];

                const mapped = statuses
                    .filter(
                        (status): status is string =>
                            typeof status === 'string' &&
                            status.trim().length > 0,
                    )
                    .map((status) => ({
                        value: status,
                        label: status,
                    }));

                setDemandStatusOptions([
                    { label: 'All Status', value: 'all' },
                    ...mapped,
                ]);
            } catch {
                setDemandStatusOptions([{ label: 'All Status', value: 'all' }]);
            }
        };

        void fetchDemandStatuses();
    }, []);

    useEffect(() => {
        const fetchSupplyStatuses = async () => {
            try {
                const statuses =
                    ((await ReparoApi({
                        method: 'POST',
                        url: '/purchase-orders/statuses/list',
                        body: JSON.stringify({ type: 'INCOMING' as PoType }),
                    })) as unknown as string[] | null) ?? [];

                const mapped = statuses
                    .filter(
                        (status): status is string =>
                            typeof status === 'string' &&
                            status.trim().length > 0,
                    )
                    .map((status) => ({
                        value: status,
                        label: status,
                    }));

                setSupplyStatusOptions([
                    { label: 'All Status', value: 'all' },
                    ...mapped,
                ]);
            } catch {
                setSupplyStatusOptions([{ label: 'All Status', value: 'all' }]);
            }
        };

        void fetchSupplyStatuses();
    }, []);

    const mapListItemToRow = useCallback(
        (item: PurchaseOrderListItemResponse): PurchaseOrderRow => {
            const sideFromType = item.type === 'INCOMING' ? 'supply' : 'demand';
            return {
                id: item.purchaseOrderId,
                poNumber: item.purchaseOrderId,
                channelId: item.channel,
                vendor: item.vendor,
                totalQuantity: item.totalQuantity ?? item.itemsCount ?? 0,
                actualQuantity: item.actualQuantity ?? 0,
                excessQuantity: item.excessQuantity ?? 0,
                orderDate: formatDateDisplay(item.orderDate),
                expiryDate: formatDateDisplay(item.expiryDate),
                deliveryDate: formatDateDisplay(item.actualDeliveryDate),
                status: normalizeStatus(item.status),
                side: sideFromType,
                poRefNum: item.poRefNum,
            };
        },
        [],
    );

    const fetchDemandPos = useCallback(async () => {
        setLoadingDemand(true);
        try {
            const payload = {
                startDate: demandFilters.range.startDate,
                endDate: demandFilters.range.endDate,
                type: 'OUTGOING' as PoType,
                channels:
                    demandFilters.channel === 'all'
                        ? []
                        : [getSelectedChannelLabel()],
                status: formatStatusForApi(demandFilters.status),
                search: demandFilters.search || undefined,
                page: demandFilters.page,
                pageSize: demandFilters.pageSize,
            };

            const response = (await ReparoApi({
                method: 'POST',
                url: '/purchase-orders/list',
                body: JSON.stringify(payload),
            })) as unknown as PurchaseOrderListResponse | null;

            const demandRows = (response?.data ?? []).map(mapListItemToRow);
            setDemandPos(demandRows);
            setDemandHasNext(response?.hasNext ?? false);
            setDemandTotal(response?.total ?? 0);
            capturePO(payload as unknown as Record<string, unknown>);
        } catch (error) {
            console.error('Failed to fetch demand POs', error);
            setDemandPos([]);
        } finally {
            setLoadingDemand(false);
        }
    }, [
        demandFilters,
        mapListItemToRow,
        getSelectedChannelLabel,
        formatStatusForApi,
    ]);

    const fetchSupplyPos = useCallback(async () => {
        setLoadingSupply(true);
        try {
            const payload = {
                startDate: supplyFilters.range.startDate,
                endDate: supplyFilters.range.endDate,
                type: 'INCOMING' as PoType,
                vendors:
                    supplyFilters.vendor === 'all'
                        ? []
                        : [supplyFilters.vendor],
                // status: formatStatusForApi(supplyStatus),
                status: formatStatusForApi(supplyFilters.status),
                search: supplyFilters.search || undefined,
                fulfilment:
                    supplyFilters.fulfilment && supplyFilters.fulfilment !== 'all'
                        ? supplyFilters.fulfilment
                        : undefined,
                page: supplyFilters.page,
                pageSize: supplyFilters.pageSize,
            };

            const response = (await ReparoApi({
                method: 'POST',
                url: '/purchase-orders/list',
                body: JSON.stringify(payload),
            })) as unknown as PurchaseOrderListResponse | null;

            const supplyRows = (response?.data ?? []).map(mapListItemToRow);
            setSupplyPos(supplyRows);
            setSupplyHasNext(response?.hasNext ?? false);
            setSupplyTotal(response?.total ?? 0);
            setWmsType(response?.wmsType);
            capturePO(payload as unknown as Record<string, unknown>);
        } catch (error) {
            console.error('Failed to fetch supply POs', error);
            setSupplyPos([]);
        } finally {
            setLoadingSupply(false);
        }
    }, [mapListItemToRow, supplyFilters, formatStatusForApi]);

    useEffect(() => {
        void fetchDemandPos();
    }, [fetchDemandPos]);

    useEffect(() => {
        void fetchSupplyPos();
    }, [fetchSupplyPos]);

    useEffect(() => {
        if (!showAISideBar) {
            abortStream();
        }
    }, [showAISideBar, abortStream]);

    // Info: Clear AI context on unmount to prevent persistence
    useEffect(() => {
        return () => {
            setAiContext(null);
        };
    }, [setAiContext]);

    const handleSideChange = (value: string) => {
        setSide(value as 'demand' | 'supply');
        setSelectedRow(null);
        setDeepLinkPoId(null);
    };

    // DataSheet columns for PO table
    const getPoColumns = useCallback((view: 'demand' | 'supply'): DataSheetColumn<PurchaseOrderRow>[] => [
        {
            key: 'poNumber',
            header: 'PO Number',
            width: 160,
            render: (value, row) => (
                <button
                    type="button"
                    onClick={() => {
                        setDeepLinkPoId(null);
                        setSelectedRow(row);
                    }}
                    style={{
                        fontWeight: 600,
                        color: 'rgb(3, 105, 161)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'none',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                >
                    {value}
                </button>
            ),
        },
        ...(view === 'supply' && wmsType === 'easyecom' ? [{
            key: 'poRefNum' as keyof PurchaseOrderRow,
            header: 'PO Ref No.',
            width: 140,
            render: (value: PurchaseOrderRow[keyof PurchaseOrderRow]) => (value as string) || '-',
        }] : []),
        {
            key: view === 'demand' ? 'channelId' : 'vendor',
            header: view === 'demand' ? 'Channel' : 'Vendor',
            width: 140,
            render: (value, row) => {
                if (view === 'demand') {
                    return channelLabelLookup.get(row.channelId || '') || row.channelId || '-';
                }
                return row.vendor || '-';
            },
        },
        {
            key: 'totalQuantity',
            header: 'Total PO Quantity',
            align: 'right',
            width: 130,
            render: (value) => (
                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {value != null ? formatCount(value) : '—'}
                </span>
            ),
        },
        {
            key: 'actualQuantity',
            header: view === 'demand' ? 'Dispatched Quantity' : 'Received Quantity',
            align: 'right',
            width: 150,
            render: (value, row) => (
                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {value != null ? formatCount(value) : '—'}
                    {view === 'supply' && (row.excessQuantity ?? 0) > 0 ? (
                        <span
                            className="ml-1.5 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700"
                            title="Units received via excess GRN bookings"
                        >
                            {formatCount(row.excessQuantity)} excess
                        </span>
                    ) : null}
                </span>
            ),
        },
        {
            key: 'orderDate',
            header: 'Order Date',
            width: 120,
        },
        {
            key: 'expiryDate',
            header: view === 'demand' ? 'Expiry Date' : 'Expected Date',
            width: 120,
            render: (value) => value || '-',
        },
        // TODO: Re-enable delivery date column when ready
        // ...(view === 'supply' ? [{
        //     key: 'deliveryDate' as keyof PurchaseOrderRow,
        //     header: 'Delivery Date',
        //     width: 120,
        //     render: (value: PurchaseOrderRow[keyof PurchaseOrderRow]) => (value as string) || '-',
        // }] : []),
        {
            key: 'status',
            header: 'Status',
            width: 150,
            render: (value) => {
                return <StatusPill status={value} />;
            },
        },
    ], [channelLabelLookup, wmsType]);

    const handleDownload = useCallback(async () => {
        const isDemand = side === 'demand';

        const payload = isDemand
            ? {
                  startDate: demandFilters.range.startDate,
                  endDate: demandFilters.range.endDate,
                  type: 'OUTGOING' as PoType,
                  channels: demandFilters.channel === 'all' ? [] : [getSelectedChannelLabel()],
                  status: formatStatusForApi(demandFilters.status),
                  search: demandFilters.search || undefined,
                  fetchAll: true,
              }
            : {
                  startDate: supplyFilters.range.startDate,
                  endDate: supplyFilters.range.endDate,
                  type: 'INCOMING' as PoType,
                  vendors: supplyFilters.vendor === 'all' ? [] : [supplyFilters.vendor],
                  status: formatStatusForApi(supplyFilters.status),
                  search: supplyFilters.search || undefined,
                  fetchAll: true,
              };

        const response = (await ReparoApi({
            method: 'POST',
            url: '/purchase-orders/list',
            body: JSON.stringify(payload),
        })) as unknown as PurchaseOrderListResponse | null;

        const rows = (response?.data ?? []).map(mapListItemToRow);
        if (!rows.length) return;

        const headers = [
            'PO Number',
            isDemand ? 'Channel' : 'Vendor',
            'Total PO Quantity',
            isDemand ? 'Dispatched Quantity' : 'Received Quantity',
            'Order Date',
            isDemand ? 'Expiry Date' : 'Expected Date',
            'Status',
        ];

        const lines = rows.map((row) => {
            const secondCol = isDemand
                ? (channelLabelLookup.get(row.channelId || '') || row.channelId || '')
                : (row.vendor || '');
            return [
                row.poNumber,
                secondCol,
                row.totalQuantity,
                row.actualQuantity ?? 0,
                row.orderDate,
                row.expiryDate || '',
                row.status,
            ].join(',');
        });

        const csv = [headers.join(','), ...lines].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${isDemand ? 'demand' : 'supply'}_purchase_orders.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [side, demandFilters, supplyFilters, channelLabelLookup, getSelectedChannelLabel, formatStatusForApi, mapListItemToRow]);

    // Current filters and data based on active side
    const currentRows = side === 'demand' ? demandPos : filteredSupplyPos;
    const currentLoading = side === 'demand' ? loadingDemand : loadingSupply;
    const currentFilters = side === 'demand' ? demandFilters : supplyFilters;
    const currentTotal = side === 'demand' ? demandTotal : supplyTotal;

    return (
        <div className="flex h-full w-full flex-col page-shell">
            {/* Shared PageHeader Component */}
            <PageHeader
                icon="🛒"
                title="Purchase Orders"
                subtitle="Manage and track orders across the supply chain"
                actions={<AskAiButton />}
            />

            {/* DataSheet with tabs for Demand/Supply */}
            <DataSheet<PurchaseOrderRow>
                className="flex-1 min-h-0"
                data={currentRows}
                columns={getPoColumns(side)}
                getRowClassName={(row) => {
                    if (side !== 'supply') return '';
                    const received = row.actualQuantity ?? 0;
                    // Aligned with the Fulfilment filter's semantics: shortage
                    // (receiving started, under ordered) or excess. Quantity-
                    // derived so Received-status underruns tint too; the status
                    // pill disambiguates in-flight from closed-short.
                    const hasShortage =
                        received > 0 && received < row.totalQuantity;
                    const hasExcess = (row.excessQuantity ?? 0) > 0;
                    return hasShortage ||
                        hasExcess ||
                        row.status === 'PARTIALLY_RECEIVED'
                        ? 'bg-amber-50/60'
                        : '';
                }}
                tabs={visibleTabs}
                activeTab={side}
                onTabChange={(tabId) => handleSideChange(tabId as 'demand' | 'supply')}
                onShare={isConsultant ? undefined : (tabId) => {
                    const resourceKey = tabId === 'supply'
                        ? ConsultantCapability.TABLE_PO_SUPPLY
                        : ConsultantCapability.TABLE_PO_DEMAND;
                    setShareModal({ resourceKey });
                }}
                title={side === 'demand' ? `Demand Side POs` : `Supply Side POs`}
                onAddToChat={addPOToChat}
                enableDownload
                onDownload={handleDownload}
                enableSearch
                searchPlaceholder="Search PO..."
                searchValue={currentFilters.search}
                onSearchChange={(val) => {
                    if (side === 'demand') {
                        setDemandFilters((prev) => ({ ...prev, page: 1, search: val }));
                    } else {
                        setSupplyFilters((prev) => ({ ...prev, page: 1, search: val }));
                    }
                }}
                toolbarControls={
                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing.xs }}>
                        {/* Date Range */}
                        <div style={{ position: 'relative' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    if (side === 'demand') {
                                        setShowDemandCalendar((prev) => !prev);
                                    } else {
                                        setShowSupplyCalendar((prev) => !prev);
                                    }
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: tokens.spacing.xs,
                                    height: tokens.dimensions.controlHeight,
                                    padding: `0 ${tokens.spacing.sm}px`,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    color: tokens.colors.textPrimary,
                                    backgroundColor: tokens.colors.surface,
                                    border: `1px solid ${tokens.colors.border}`,
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                }}
                            >
                                <span style={{ fontSize: 12 }}>📅</span>
                                <span>
                                    {formatDisplayRange(
                                        side === 'demand' ? demandFilters.range : supplyFilters.range
                                    )}
                                </span>
                            </button>
                            {side === 'demand' && showDemandCalendar && (
                                <DatePopover
                                    range={demandFilters.range}
                                    onClose={() => setShowDemandCalendar(false)}
                                    onApply={(range) => {
                                        setDemandFilters((prev) => ({ ...prev, page: 1, range }));
                                        setShowDemandCalendar(false);
                                    }}
                                />
                            )}
                            {side === 'supply' && showSupplyCalendar && (
                                <DatePopover
                                    range={supplyFilters.range}
                                    onClose={() => setShowSupplyCalendar(false)}
                                    onApply={(range) => {
                                        setSupplyFilters((prev) => ({ ...prev, page: 1, range }));
                                        setShowSupplyCalendar(false);
                                    }}
                                />
                            )}
                        </div>

                        {/* Channel/Vendor Filter */}
                        {side === 'demand' ? (
                            <FilterDropdown
                                value={demandFilters.channel}
                                onChange={(val) => setDemandFilters((prev) => ({ ...prev, page: 1, channel: val }))}
                                options={channelOptions}
                                placeholder="Channel"
                            />
                        ) : (
                            <FilterDropdown
                                value={supplyFilters.vendor}
                                onChange={(val) => setSupplyFilters((prev) => ({ ...prev, page: 1, vendor: val }))}
                                options={vendorOptions}
                                placeholder="Vendor"
                            />
                        )}

                        {/* Status Filter */}
                        <FilterDropdown
                            value={side === 'demand' ? demandFilters.status : supplyFilters.status}
                            onChange={(val) => {
                                if (side === 'demand') {
                                    setDemandFilters((prev) => ({ ...prev, page: 1, status: val }));
                                } else {
                                    setSupplyFilters((prev) => ({ ...prev, page: 1, status: val }));
                                }
                            }}
                            options={side === 'demand' ? demandStatusOptions : supplyStatusOptions}
                            placeholder="Status"
                        />

                        {/* Fulfilment filter (supply side): line-level received-vs-ordered lens */}
                        {side === 'supply' ? (
                            <FilterDropdown
                                value={supplyFilters.fulfilment ?? 'all'}
                                onChange={(val) =>
                                    setSupplyFilters((prev) => ({
                                        ...prev,
                                        page: 1,
                                        fulfilment: val,
                                    }))
                                }
                                options={[
                                    { label: 'All Fulfilment', value: 'all' },
                                    { label: 'Shortage', value: 'shortage' },
                                    { label: 'Excess', value: 'excess' },
                                    { label: 'Any Mismatch', value: 'any_mismatch' },
                                    { label: 'Fulfilled Exactly', value: 'fulfilled_exactly' },
                                ]}
                                placeholder="Fulfilment"
                            />
                        ) : null}
                    </div>
                }
                isLoading={currentLoading}
                maxHeight="auto"
                getRowKey={(row) => row.id}
                emptyState="No purchase orders found."
                footer={
                    <DataSheetPagination
                        currentPage={currentFilters.page}
                        totalPages={Math.ceil(currentTotal / currentFilters.pageSize) || 1}
                        pageSize={currentFilters.pageSize}
                        totalItems={currentTotal}
                        pageSizeOptions={[20, 50, 100]}
                        onPageChange={(p) => {
                            if (side === 'demand') {
                                setDemandFilters((prev) => ({ ...prev, page: p }));
                            } else {
                                setSupplyFilters((prev) => ({ ...prev, page: p }));
                            }
                        }}
                        onPageSizeChange={(size) => {
                            if (side === 'demand') {
                                setDemandFilters((prev) => ({ ...prev, page: 1, pageSize: size }));
                            } else {
                                setSupplyFilters((prev) => ({ ...prev, page: 1, pageSize: size }));
                            }
                        }}
                    />
                }
            />

            <PurchaseOrderDetailDrawer
                poId={selectedRow?.id ?? deepLinkPoId}
                initialRow={selectedRow ?? undefined}
                side={selectedRow?.side ?? 'supply'}
                channelLabelLookup={channelLabelLookup}
                onClose={() => {
                    setSelectedRow(null);
                    setDeepLinkPoId(null);
                }}
            />

            {shareModal && (
                <ShareModal
                    opened={!!shareModal}
                    onClose={() => setShareModal(null)}
                    resourceKey={shareModal.resourceKey}
                    merchantId={currentUser?.organizationId ?? ''}
                />
            )}
        </div>
    );
};

export default PurchaseOrdersPage;
